// src/components/SchoolSearchField.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

// Sectoren zoals DUO ze bij de school vastlegt. Let op: dit is een andere lijst dan
// de zelf opgegeven `educationSector` van de gebruiker (PO/VO/MBO/HBO/S(VO)).
const SECTOR_LABELS = { PO: 'PO', VO: 'VO', MBO: 'MBO', HO: 'HO', SBO: 'SBO', SO: 'SO' };

function titleCase(s) {
  if (!s) return '';
  return s
    .toLowerCase()
    .replace(/(^|[\s'-])([a-zà-ÿ])/g, (m, p1, p2) => p1 + p2.toUpperCase());
}

function schoolLine(s) {
  const addr = [s.street, [s.postcode, titleCase(s.city)].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return addr + (s.sector ? ` · ${SECTOR_LABELS[s.sector] || s.sector}` : '');
}

// Toont het opleiding-veld voor MBO / HBO / WO (hoger onderwijs)
function needsOpleiding(selectedSector, educationSector) {
  if (selectedSector) return selectedSector === 'MBO' || selectedSector === 'HO';
  return educationSector === 'MBO' || educationSector === 'HBO';
}

const linkBtn = {
  background: 'none',
  border: 'none',
  color: '#4945ff',
  cursor: 'pointer',
  padding: '0.35rem 0',
  fontSize: '0.85rem',
};

const dropdownStyle = {
  listStyle: 'none',
  margin: '0.25rem 0 0',
  padding: 0,
  position: 'absolute',
  zIndex: 20,
  width: '100%',
  maxHeight: '300px',
  overflowY: 'auto',
  background: '#fff',
  border: '1px solid #d9d9e3',
  borderRadius: '6px',
  boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
};
const hintItem = { padding: '0.6rem 0.75rem', color: '#888' };
const resultItem = { padding: '0.55rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f4' };

/**
 * Gecontroleerd school-zoekveld op basis van BRIN (DUO).
 * value: { brin, schoolName, city, opleiding }
 * onChange(patch): partial update van bovenstaande velden.
 */
function SchoolSearchField({ brin, schoolName, city, opleiding, educationSector, onChange, idPrefix = 'reg', initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(() => !!schoolName && !brin);
  const [selectedSector, setSelectedSector] = useState(null);
  const blurTimer = useRef(null);

  const hasSelection = !!brin && !!schoolName;

  useEffect(() => {
    if (manual || hasSelection) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${STRAPI_URL}/api/schools/search?q=${encodeURIComponent(q)}`);
        if (!cancelled) {
          setResults(res.data?.results ?? []);
          setOpen(true);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, manual, hasSelection]);

  const selectSchool = (s) => {
    setSelectedSector(s.sector || null);
    setOpen(false);
    setQuery('');
    setResults([]);
    onChange({ brin: s.brin || '', schoolName: s.name, city: titleCase(s.city) });
  };

  const clearSelection = () => {
    setSelectedSector(null);
    setManual(false);
    setQuery('');
    onChange({ brin: '', schoolName: '', city: '' });
  };

  const showOpleiding = needsOpleiding(selectedSector, educationSector);

  return (
    <>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-school`}>School</label>

        {hasSelection ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '0.75rem',
              padding: '0.6rem 0.75rem',
              border: '1px solid #d9d9e3',
              borderRadius: '6px',
              background: '#f6f6f9',
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{schoolName}</div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {titleCase(city)}
                {city && brin ? ' · ' : ''}
                {brin ? `BRIN ${brin}` : ''}
              </div>
            </div>
            <button type="button" onClick={clearSelection} style={{ ...linkBtn, whiteSpace: 'nowrap' }}>
              Wijzig
            </button>
          </div>
        ) : manual ? (
          <>
            <input
              id={`${idPrefix}-school`}
              name="organization"
              type="text"
              placeholder="Naam van je school of organisatie"
              value={schoolName}
              onChange={(e) => onChange({ schoolName: e.target.value })}
              autoComplete="organization"
            />
            <button type="button" onClick={() => { setManual(false); onChange({ schoolName: '', city: '' }); }} style={linkBtn}>
              ← Toch zoeken in de lijst
            </button>
          </>
        ) : (
          <div style={{ position: 'relative' }}>
            <input
              id={`${idPrefix}-school`}
              type="text"
              placeholder="Zoek je school op naam of plaats…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
              autoComplete="off"
            />
            {open && (
              <ul style={dropdownStyle}>
                {loading && <li style={hintItem}>Zoeken…</li>}
                {!loading && query.trim().length < 2 && (
                  <li style={hintItem}>Typ minstens 2 letters om te zoeken…</li>
                )}
                {!loading && query.trim().length >= 2 && results.length === 0 && (
                  <li style={hintItem}>Geen school gevonden</li>
                )}
                {results.map((s) => (
                  <li
                    key={`${s.id}`}
                    onMouseDown={() => { if (blurTimer.current) clearTimeout(blurTimer.current); selectSchool(s); }}
                    style={resultItem}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f6f6f9')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                  >
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>{schoolLine(s)}</div>
                  </li>
                ))}
                <li
                  onMouseDown={() => {
                    if (blurTimer.current) clearTimeout(blurTimer.current);
                    setOpen(false);
                    // neem de al getypte tekst mee als schoolnaam
                    if (query.trim()) onChange({ schoolName: query.trim() });
                    setManual(true);
                  }}
                  style={{ ...resultItem, borderBottom: 'none', borderTop: '1px solid #e6e6ee', color: '#4945ff', fontWeight: 600 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f6f6f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                >
                  Mijn school staat er niet bij. Zelf invullen
                </li>
              </ul>
            )}
          </div>
        )}
      </div>

      {manual && (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-city`}>Plaats</label>
          <input
            id={`${idPrefix}-city`}
            name="address-level2"
            type="text"
            value={city || ''}
            onChange={(e) => onChange({ city: e.target.value })}
            autoComplete="address-level2"
          />
        </div>
      )}

      {showOpleiding && (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-opleiding`}>Opleiding (optioneel)</label>
          <input
            id={`${idPrefix}-opleiding`}
            type="text"
            placeholder="Bijv. Onderwijskunde, Verpleegkunde…"
            value={opleiding || ''}
            onChange={(e) => onChange({ opleiding: e.target.value })}
          />
        </div>
      )}
    </>
  );
}

export default SchoolSearchField;
